import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { TourProvider } from '@reactour/tour';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ToastContainer } from 'react-toastify';
import ThemeContext from '../contexts/themeContext';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import { ToastCloseButton } from '../components/bootstrap/Toasts';
import { requestNotificationPermission, showNotification } from '../utils/notifications';
import { socket } from '../socket';
import { toast } from 'react-toastify';

const App = () => {
	getOS();

	dayjs.extend(localizedFormat);
	dayjs.extend(relativeTime);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};
	// Disable Ctrl + P
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			// For Windows/Linux: Ctrl + P
			// For Mac: Meta + P (Meta is the Command key)
			const isCtrlP = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'p';

			if (isCtrlP) {
				event.preventDefault();
				console.log('Print shortcut disabled.');
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
			document.documentElement.setAttribute('data-bs-theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
			document.documentElement.removeAttribute('data-bs-theme');
		};
	}, [darkModeStatus]);

	useEffect(() => {
		requestNotificationPermission();

		const handler = (data: any) => {
			console.log('Notification from socket:', data);

			// 🪟 system popup
			showNotification(data?.title || 'New Notification', {
				body: data?.message || 'You got an update',
				icon: '/logo192.png',
			});

			// 🔔 Toast based on type
			switch (data?.type) {
				case 'success':
					toast.success(data?.message, {
						closeOnClick: true,
						closeButton: true,
					});
					break;
				case 'warning':
					toast.warn(data?.message, {
						closeOnClick: true,
						closeButton: true,
					});
					break;
				case 'error':
					toast.error(data?.message, {
						closeOnClick: true,
						closeButton: true,
					});
					break;
				default:
					toast.info(data?.message, {
						closeOnClick: true,
						closeButton: true,
					});
			}

			// 🔥 update header count
			window.dispatchEvent(new CustomEvent('NEW_NOTIFICATION', { detail: data }));
		};

		socket.off('notification'); // 🔥 important
		socket.on('notification', handler);

		return () => {
			socket.off('notification', handler);
		};
	}, []);

	useEffect(() => {
		let user: any = {};

		try {
			const raw = localStorage.getItem('neels_authUsername');

			if (raw && raw !== 'undefined' && raw !== 'null') {
				user = JSON.parse(raw);
			}
		} catch (err) {
			console.warn('Invalid user data in localStorage', err);
			user = {};
		}

		if (user && socket.connected) {
			socket.emit('authenticate', {
				userId: user?._id || user?.id,
				socketId: socket.id,
			});
		}
	}, []);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	// @ts-ignore
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	return (
		<ThemeProvider theme={theme}>
			<TourProvider steps={steps} styles={styles} showNavigation={false} showBadge={false}>
				<div
					ref={ref}
					className='app'
					style={{
						backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
						zIndex: fullScreenStatus ? 1 : undefined,
						overflow: fullScreenStatus ? 'scroll' : undefined,
					}}>
					<AsideRoutes />
					<Wrapper />
				</div>
				<Portal id='portal-notification'>
					<ReactNotifications />
				</Portal>
				<ToastContainer closeButton={ToastCloseButton} toastClassName='toast show' />
			</TourProvider>
		</ThemeProvider>
	);
};

export default App;
